import {
  Children,
  cloneElement,
  createContext,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn'
import { buttonVariants } from '../button/button'

const buttonGroupVariants = cva('inline-flex', {
  variants: {
    gap: {
      none: 'gap-0',
      sm: 'gap-0.5',
    },
  },
  defaultVariants: {
    gap: 'sm',
  },
})

interface ButtonGroupContextValue {
  value: string | undefined
  onValueChange: ((value: string) => void) | undefined
  disabled: boolean
}

const ButtonGroupContext = createContext<ButtonGroupContextValue | null>(null)

function useButtonGroupContext() {
  const context = useContext(ButtonGroupContext)
  if (!context) {
    throw new Error('ButtonGroupItem must be used within a ButtonGroup')
  }
  return context
}

interface ButtonGroupProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof buttonGroupVariants> {
  /** Currently selected value */
  value?: string
  /** Callback fired when selection changes */
  onValueChange?: (value: string) => void
  /** Disables all items in the group */
  disabled?: boolean
  children: ReactNode
}

/**
 * A group of connected toggle buttons where only one can be selected.
 *
 * @example
 * ```tsx
 * <ButtonGroup value={language} onValueChange={setLanguage}>
 *   <ButtonGroupItem value="curl">cURL</ButtonGroupItem>
 *   <ButtonGroupItem value="python">Python</ButtonGroupItem>
 *   <ButtonGroupItem value="javascript">JavaScript</ButtonGroupItem>
 * </ButtonGroup>
 * ```
 */
function ButtonGroup({
  className,
  gap,
  value,
  onValueChange,
  disabled = false,
  children,
  ...props
}: ButtonGroupProps) {
  const validChildren = Children.toArray(children).filter(isValidElement)
  const childCount = validChildren.length

  return (
    <ButtonGroupContext.Provider value={{ value, onValueChange, disabled }}>
      <div role='radiogroup' className={cn(buttonGroupVariants({ gap }), className)} {...props}>
        {validChildren.map((child, index) => {
          const position: 'first' | 'middle' | 'last' | 'only' =
            childCount === 1
              ? 'only'
              : index === 0
                ? 'first'
                : index === childCount - 1
                  ? 'last'
                  : 'middle'

          return cloneElement(child as ReactElement<ButtonGroupItemProps>, {
            _position: position,
          })
        })}
      </div>
    </ButtonGroupContext.Provider>
  )
}

const buttonGroupItemVariants = cva('whitespace-nowrap', {
  variants: {
    active: {
      true: buttonVariants({ variant: 'active', size: 'md' }),
      false: buttonVariants({ variant: 'default', size: 'md' }),
    },
    position: {
      only: '',
      first: 'rounded-r-none',
      middle: 'rounded-none',
      last: 'rounded-l-none',
    },
  },
  defaultVariants: {
    active: false,
    position: 'only',
  },
})

interface ButtonGroupItemProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Value associated with this item */
  value: string
  /** Disables this specific item */
  disabled?: boolean
  /** @internal Position within the group, set automatically */
  _position?: 'first' | 'middle' | 'last' | 'only'
}

/**
 * An individual item within a ButtonGroup.
 */
function ButtonGroupItem({
  className,
  value,
  disabled: itemDisabled,
  _position = 'only',
  children,
  ...props
}: ButtonGroupItemProps) {
  const context = useButtonGroupContext()
  const isActive = context.value === value
  const isDisabled = context.disabled || itemDisabled

  const handleClick = () => {
    if (!isDisabled && context.onValueChange) {
      context.onValueChange(value)
    }
  }

  return (
    <button
      type='button'
      role='radio'
      aria-checked={isActive}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(buttonGroupItemVariants({ active: isActive, position: _position }), className)}
      {...props}
    >
      {children}
    </button>
  )
}

ButtonGroup.displayName = 'ButtonGroup'
ButtonGroupItem.displayName = 'ButtonGroupItem'

export { ButtonGroup, ButtonGroupItem, buttonGroupItemVariants, buttonGroupVariants }
